import React from "react";
import Image1 from "../../../Asset/image1.png";
import Image2 from "../../../Asset/image2.png";
import Image3 from "../../../Asset/image3.png";
import Image4 from "../../../Asset/image6.png";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useColorMode } from "@chakra-ui/react";
import { motion } from "framer-motion";

import { FaArrowRight, FaEnvelope } from "react-icons/fa";
const AccompagnementHandicap = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  
  const services = [
    {
      title: "Aide aux gestes quotidiens",
      subtitle: "Soins et assistance à domicile",
      image: Image1,
      description:
        "Nos intervenants accompagnent les personnes en perte d’autonomie dans leurs gestes du quotidien : aide à la toilette, à l’habillage, à la préparation des repas et à la mobilité. Chaque accompagnement est personnalisé selon les besoins spécifiques de chacun.",
    },
    {
      title: "Aide à la mobilité en extérieur",
      subtitle: "Déplacements facilités et sécurisés",
      image: Image2,
      description:
        "Nous facilitons vos déplacements extérieurs grâce à un accompagnement humain et une flotte de véhicules adaptés PMR. Que ce soit pour des rendez-vous médicaux ou des activités sociales, vous restez autonome et mobile.",
    },
    {
      title: "Accompagnement à la vie sociale",
      subtitle: "Maintenir le lien et le plaisir de vivre",
      image: Image3,
      description:
        "Parce que le lien social est essentiel, nous proposons un accompagnement pour vos loisirs, sorties culturelles ou activités de groupe. Nos équipes favorisent la convivialité, l’écoute et le partage.",
    },
    {
      title: "Assistance administrative",
      subtitle: "Simplifier vos démarches au quotidien",
      image: Image4,
      description:
        "Nous vous aidons dans la gestion de vos documents, démarches administratives et usages numériques. Notre accompagnement permet de préserver votre autonomie face aux formalités parfois complexes.",
    },
  ];

  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    <>
      <Helmet>
        <title>Accompagnement du handicap — Hetep Iaout Services</title>
        <meta name="description" content="Accompagnement à domicile pour personnes handicapées et atteintes de maladies rares en Île-de-France. Auxiliaires de vie formés, certifiés Cap'Handéo. Devis gratuit." />
        <link rel="canonical" href="https://www.hisvie.com/personnes-handicapees/" />
        <meta property="og:title" content="Accompagnement du handicap — Hetep Iaout Services" />
        <meta property="og:description" content="Accompagnement à domicile pour personnes handicapées et atteintes de maladies rares en Île-de-France." />
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}`}>
        
        {/* --- NOUVEAU HERO : 100% TYPOGRAPHIQUE ET MINIMALISTE --- */}
        <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 max-w-5xl mx-auto flex flex-col items-center text-center">
          
          {/* Glow subtil en arrière-plan pour habiller le fond sans image */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-blue-400/10 rounded-full blur-[100px] pointer-events-none -z-10"></div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col items-center"
          >
            <div className={`inline-block px-4 py-1.5 rounded-full border mb-8 text-xs font-bold uppercase tracking-widest shadow-sm ${isDark ? 'bg-blue-900/30 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
              Spécialité & Dévouement
            </div>
            
            <h1 className={`text-5xl md:text-7xl lg:text-[5rem] font-black mb-8 leading-[1.1] tracking-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
              Accompagnement du <br/>
              <span className="text-blue-600 font-serif italic font-light">Handicap</span>
            </h1>
            
            <p className={`text-lg md:text-2xl leading-relaxed mb-12 max-w-3xl mx-auto font-light ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Nos services sont pensés pour faciliter le quotidien des personnes en situation de handicap ou de perte d’autonomie, tout en favorisant leur indépendance et leur bien-être à domicile.
            </p>

            {/* Petite ligne de séparation élégante */}
            <div className="w-16 md:w-24 h-1 bg-blue-600 rounded-full"></div>
          </motion.div>
        </section>

        {/* --- NOUVEAU LAYOUT DES SERVICES : "OVERLAPPING CARDS" (ÉDITORIAL) --- */}
        <section className="max-w-7xl mx-auto px-6 py-20">
          <div className="space-y-32 md:space-y-40">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={sectionVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className={`flex flex-col lg:flex-row items-center relative ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* L'image panoramique (60% de la largeur) */}
                <div className="w-full lg:w-2/3 relative z-0">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="rounded-3xl shadow-xl object-cover w-full h-[350px] md:h-[500px]"
                  />
                </div>

                {/* La carte de texte qui se superpose (Overlapping) */}
                <div className={`w-full lg:w-5/12 relative z-10 mt-[-50px] lg:mt-0 ${index % 2 === 0 ? 'lg:ml-[-10%]' : 'lg:mr-[-10%]'}`}>
                  <div className={`p-8 md:p-12 rounded-[2rem] shadow-2xl border backdrop-blur-xl relative overflow-hidden ${isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-100'}`}>
                    
                    {/* Grand numéro en filigrane (01, 02, etc.) */}
                    <div className={`absolute -top-6 -right-4 text-[120px] font-black leading-none opacity-5 pointer-events-none select-none ${isDark ? 'text-white' : 'text-blue-900'}`}>
                      0{index + 1}
                    </div>

                    <div className="relative z-10">
                      <h3 className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-3">
                        {service.subtitle}
                      </h3>
                      <h2 className={`text-3xl md:text-4xl font-black mb-6 leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                        {service.title}
                      </h2>
                      <div className="w-12 h-1 bg-blue-600 rounded-full mb-6"></div>
                      <p className={`text-base md:text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
{/* --- CTA FINAL : ACCOMPAGNEMENT & DEVIS --- */}
<motion.section 
    initial="hidden" 
    whileInView="visible" 
    viewport={{ once: true }} 
    variants={sectionVariants} // Doit être défini localement
    className="max-w-6xl mx-auto px-4 pb-24"
>
    <div className={`relative overflow-hidden rounded-[3rem] p-10 md:p-16 flex flex-col lg:flex-row items-center gap-10 border shadow-2xl transition-all duration-500 ${
        isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-blue-900/5'
    }`}>
        
        {/* Contenu texte */}
        <div className="flex-1 z-10 text-center md:text-left">
            <h2 className={`text-3xl md:text-5xl font-black mb-4 tracking-tighter leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                Besoin d’un <span className="text-blue-600 font-serif italic font-light">accompagnement personnalisé ?</span>
            </h2>
            <p className={`text-base md:text-lg max-w-xl mx-auto md:mx-0 font-light leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Contactez-nous dès aujourd’hui pour obtenir un devis gratuit et une solution adaptée à vos besoins.
            </p>
        </div>

        {/* Boutons d'action (Ergonomie identique à ton exemple) */}
        <div className="flex flex-col sm:flex-row w-full lg:w-auto gap-4 z-10 shrink-0">
            <Link to="/demande-de-devis" className="w-full sm:w-auto">
                <button className="flex items-center justify-center gap-3 bg-blue-600 text-white px-8 py-5 rounded-2xl font-black uppercase text-[11px] tracking-widest hover:bg-blue-700 transition-all w-full shadow-xl shadow-blue-600/20">
                    Devis Gratuit
                </button>
            </Link>
            
            <motion.a 
                href="mailto:his@donnerlavieauxannees.com"
                whileHover={{ scale: 1.02 }} 
                whileTap={{ scale: 0.98 }}
                className={`flex items-center justify-center gap-3 px-8 py-5 rounded-2xl font-black uppercase text-[11px] tracking-widest transition-all border w-full sm:w-auto ${
                    isDark 
                    ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' 
                    : 'bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100'
                }`}
            >
                <FaEnvelope size={14} /> Email
            </motion.a>
        </div>

        {/* Décoration en arrière-plan (Effet HIS) */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
    </div>
</motion.section>

      </div>
    </>
  );
};

export default AccompagnementHandicap;